import { useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import { useAiSuggest, parseAiOpsResponse } from '../state/useAiSuggest.js';
import { proposeObjectEditPrompt, type EditableKind } from '../state/aiPrompts.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { navigateSeedOp } from '../state/hashRouter.js';
import type { SandboxOp } from '../state/sandboxOps.js';
import { checkPiiLikeContent } from '../state/piiPatternGuard.js';
import { Icon } from './Icon.js';
import { ErrorState } from './ErrorState.js';

/**
 * Caution-only check (never blocks) for the one real reference-like field
 * across the 5 update-* op kinds: domain. Everything else patchable is
 * either free text or a closed enum validateOp already checks structurally.
 */
export function checkEditProposalDomainReference(op: SandboxOp, manifest: AtlasManifest): string | null {
  if (!('domain' in op) || op.domain === undefined) return null;
  const known = new Set(manifest.domains.map((d) => d.id));
  if (known.has(op.domain)) return null;
  return `Atlas proposed domain "${op.domain}", which doesn't match any existing domain — double-check before accepting.`;
}

export interface AskAtlasToEditBoxProps {
  kind: EditableKind;
  selectorId: string;
  currentObject: Record<string, unknown>;
  manifest: AtlasManifest;
  onAsk: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
}

/**
 * Inline "propose a change" box — distinct from the page's separate "Ask
 * Atlas" action-row button (a question ABOUT the object; this is a proposed
 * CHANGE to it). Typing alone never submits — only the explicit button click
 * does, matching NewDesignChangeView's established describe-it-box pattern.
 * No keyword-heuristic fallback on AI failure: unlike the create-flow (which
 * can fall back to guessKind), there's no sensible non-AI substitute for
 * "what should this patch contain" — the box just surfaces the error.
 */
export function AskAtlasToEditBox({
  kind,
  selectorId,
  currentObject,
  manifest,
  onAsk,
}: AskAtlasToEditBoxProps): React.JSX.Element {
  const [instruction, setInstruction] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const ai = useAiSuggest(onAsk);

  const handlePropose = async (): Promise<void> => {
    const trimmed = instruction.trim();
    if (!trimmed) return;
    setParseError(null);
    const result = await ai.suggest(proposeObjectEditPrompt(kind, selectorId, currentObject, trimmed, manifest));
    if (!result) return; // ai.error already set by useAiSuggest
    const entries = parseAiOpsResponse(result.answer);
    if (entries.length === 0) {
      setParseError("Atlas couldn't draft a valid edit from that — try rephrasing, or edit it directly in the sandbox.");
      return;
    }
    const entry = entries[0]!;
    const domainCaution = checkEditProposalDomainReference(entry.op, manifest);
    const piiCaution = checkPiiLikeContent(currentObject, kind, selectorId, manifest);
    const caution = domainCaution ?? piiCaution;
    if (caution) setParseError(caution); // reuses the same ErrorState slot for the caution text
    navigateSeedOp(entry);
  };

  return (
    <div className="ai-edit-box">
      <div className="ai-edit-box-head">
        <Icon name="auto_awesome" size="sm" className="ai-edit-box-spark" />
        <span className="ai-edit-box-label">Propose a change with Atlas</span>
      </div>
      <p className="ai-edit-box-hint">
        Describe what should change — Atlas drafts an edit for you to review in your sandbox before anything is saved.
      </p>
      <textarea
        className="ai-edit-box-input"
        placeholder={`Describe the change you want to propose for this ${kind}…`}
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
      />
      <div className="ai-edit-box-actions">
        <span className="ai-edit-box-note">Opens as a draft in Sandbox · nothing changes until you accept it</span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={ai.busy || !instruction.trim()}
          onClick={() => void handlePropose()}
        >
          <Icon name="auto_awesome" size="sm" />
          {ai.busy ? 'Asking Atlas…' : 'Ask Atlas to edit this'}
        </button>
        {ai.busy && <span role="status" aria-label="Asking Atlas" className="spinner" />}
      </div>
      {(ai.error ?? parseError) && <ErrorState message={(ai.error ?? parseError)!} variant="inline" />}
    </div>
  );
}
